import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { departmentsApi, departmentQueryKeys } from "../api/departments";
import { OptionCombobox } from "./OptionCombobox";

const NONE_LABEL = "— Không thuộc khoa —";

/**
 * Khoa (Department) picker, populated from GET /v1/departments (active only).
 * Binds a nullable departmentId; empty value = "— Không thuộc khoa —".
 *
 * <p>The listbox behaviour lives in {@link OptionCombobox} — see its note for why a native
 * `&lt;select&gt;` could not meet what QA asked for (#18593). This file is now only "which options,
 * and what does empty mean".
 */
export function DepartmentSelect({
  value,
  onChange,
  id,
  disabled,
  className,
}: {
  value: string | null;
  onChange: (departmentId: string | null) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
}) {
  const query = useQuery({
    queryKey: departmentQueryKeys.options(),
    queryFn: () => departmentsApi.options(),
  });

  /** "— Không thuộc khoa —" first, mirroring the empty option the select used to render. */
  const options = useMemo(
    () => [
      { id: "", label: NONE_LABEL },
      ...(query.data ?? []).map((d) => ({
        id: d.id,
        label: `${d.name}${d.code ? ` (${d.code})` : ""}`,
      })),
    ],
    [query.data]
  );

  return (
    <OptionCombobox
      options={options}
      value={value}
      onChange={(v) => onChange(v || null)}
      id={id}
      disabled={disabled}
      className={className}
    />
  );
}
